import { cn } from "@newxsystems/shared";
import type { CSSProperties } from "react";
import { resolveTootPrimitive } from "../../../../client";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import type {
  ITestimonialGridBlockProps,
  TestimonialGridBlockItem,
  TestimonialGridBlockLink,
  TestimonialGridBlockMedia,
} from "./TestimonialGridBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const Image = resolveTootPrimitive("Image");
const Link = resolveTootPrimitive("Link");

const getMediaStyle = (
  media: TestimonialGridBlockMedia | null | undefined,
): CSSProperties | undefined =>
  media?.focalPoint
    ? {
        objectPosition: `${media.focalPoint.x * 100}% ${media.focalPoint.y * 100}%`,
      }
    : undefined;

const renderRating = (rating: TestimonialGridBlockItem["rating"]) => {
  if (rating === undefined || rating === null) {
    return null;
  }

  const displayRating = `${rating}/5`;
  const roundedRating = Math.round(rating);

  return (
    <div
      aria-label={`Rating: ${displayRating}`}
      className="flex min-h-6 flex-wrap items-center gap-x-2 gap-y-1 text-[0.66rem] font-black uppercase tracking-[0.14em] text-[var(--color-toot-orange)]"
    >
      <span aria-hidden="true" className="tracking-[0.06em]">
        {"★".repeat(roundedRating)}
        <span className="text-toot-grid-strong">{"★".repeat(5 - roundedRating)}</span>
      </span>
      <span>{displayRating}</span>
    </div>
  );
};

const renderAvatar = (media: TestimonialGridBlockMedia | null | undefined) => {
  if (!media?.defaultSrc) {
    return null;
  }

  return (
    <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-[var(--radius-toot-frame)] border border-[color-mix(in_oklab,var(--color-toot-orange)_38%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-red)_10%,var(--color-toot-surface-terminal))] text-sm font-black uppercase tracking-[-0.05em] text-[var(--color-toot-orange)] shadow-lg shadow-black/30">
      <Image
        alt={media.alternateText}
        className="h-full w-full object-cover grayscale transition duration-300 group-hover/testimonial:grayscale-0 motion-reduce:transition-none"
        height={media.height ?? undefined}
        loading="lazy"
        sizes={media.sizes ?? undefined}
        src={media.defaultSrc}
        srcSet={media.srcSet ?? undefined}
        style={getMediaStyle(media)}
        width={media.width ?? undefined}
      />
    </div>
  );
};

const renderLogo = (logo: TestimonialGridBlockMedia | null | undefined) => {
  if (!logo?.defaultSrc) {
    return null;
  }

  const className =
    "flex min-h-8 max-w-[7.5rem] shrink-0 items-center justify-end rounded-[var(--radius-toot-panel)] border border-toot-grid/75 bg-toot-surface-raised/60 px-2.5 py-1.5 opacity-60 transition duration-300 group-hover/testimonial:opacity-90 motion-reduce:transition-none";

  return (
    <div className={`${className} grayscale group-hover/testimonial:grayscale-0`}>
      <Image
        alt={logo.alternateText}
        className="max-h-6 w-auto object-contain"
        height={logo.height ?? undefined}
        loading="lazy"
        sizes={logo.sizes ?? undefined}
        src={logo.defaultSrc}
        srcSet={logo.srcSet ?? undefined}
        style={getMediaStyle(logo)}
        width={logo.width ?? undefined}
      />
    </div>
  );
};

const renderLink = (link: TestimonialGridBlockLink | null | undefined) => {
  const href = link?.href;
  const label = link?.text;

  if (!href || !label) {
    return null;
  }

  return (
    <Link
      aria-label={link?.ariaLabel ?? undefined}
      className="inline-flex min-h-10 w-fit max-w-full items-center gap-2 rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_34%,transparent)] bg-toot-surface-raised/55 px-3 py-2 text-[0.66rem] font-black uppercase tracking-[0.14em] text-[var(--color-toot-orange)] transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 hover:border-[var(--color-toot-red)] hover:bg-[var(--color-toot-red)] hover:text-[var(--color-primary-foreground)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
      href={href}
      rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
      target={link?.target ?? undefined}
    >
      <span className="min-w-0 [overflow-wrap:anywhere]">{label}</span>
      <span aria-hidden="true">→</span>
    </Link>
  );
};

const TestimonialCard = ({ item }: { item: TestimonialGridBlockItem }) => {
  const meta = [item.role, item.company].filter(Boolean).join(" // ");

  return (
    <figure
      className={cn(
        "toot-heat-panel group/testimonial flex h-full min-w-0 flex-col rounded-[var(--radius-toot-frame)] p-5 text-[var(--color-toot-foreground)] transition-[border-color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:border-toot-orange/70 hover:shadow-2xl hover:shadow-black/50 sm:p-6 lg:p-7 motion-reduce:transition-none motion-reduce:hover:translate-y-0",
      )}
    >
      <div className="flex min-w-0 items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          {renderRating(item.rating)}
          {item.company ? (
            <p className="m-0 max-w-[18rem] truncate text-[0.66rem] font-black uppercase tracking-[0.18em] text-[color-mix(in_oklab,var(--color-toot-muted)_78%,transparent)]">
              {item.company}
            </p>
          ) : null}
        </div>
        {renderLogo(item.logo)}
      </div>

      <blockquote className="relative m-0 mt-6 min-w-0 flex-1 [overflow-wrap:anywhere]">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -left-1 -top-5 font-black leading-none text-[4rem] text-[color-mix(in_oklab,var(--color-toot-orange)_18%,transparent)]"
        >
          “
        </span>
        <p className="relative m-0 max-w-[58ch] text-pretty text-[1.02rem] font-bold leading-7 tracking-[-0.025em] text-[color-mix(in_oklab,var(--color-toot-foreground)_92%,transparent)] sm:text-[1.1rem] sm:leading-8">
          {item.quote}
        </p>
      </blockquote>

      <footer className="mt-6 flex min-w-0 flex-col gap-4 border-t border-[color-mix(in_oklab,var(--color-toot-grid-strong)_62%,transparent)] pt-5">
        <figcaption className="flex min-w-0 items-center gap-3.5">
          {renderAvatar(item.avatar)}
          <div className="min-w-0 flex-1">
            {item.personName ? (
              <cite className="block truncate not-italic text-sm font-black tracking-[-0.02em] text-toot-foreground md:text-[0.96rem]">
                {item.personName}
              </cite>
            ) : null}
            {meta ? (
              <div className="mt-1 text-[0.66rem] font-bold uppercase tracking-[0.14em] text-[var(--color-toot-muted)] [overflow-wrap:anywhere]">
                {meta}
              </div>
            ) : null}
          </div>
        </figcaption>
        {renderLink(item.link)}
      </footer>
    </figure>
  );
};

export const TestimonialGridBlock = ({
  anchorId,
  buttons,
  eyebrow,
  heading,

  items,
  parentClass,
  spacing,
  subheading,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) {
    return null;
  }

  const actions = buttons ?? [];
  const renderedActions =
    actions.length > 0 ? <TootActions actions={actions} alignment="center" /> : undefined;
  const hasHeader = Boolean(heading || eyebrow || subheading);
  const header = hasHeader ? (
    heading ? (
      <TootHeading
        actions={renderedActions}
        alignment="center"
        eyebrow={eyebrow}
        heading={heading}
        subheading={subheading}
      />
    ) : (
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
        {eyebrow ? <p className="toot-eyebrow">{eyebrow}</p> : null}
        {subheading ? <RichTextHtml className="toot-subheading" html={subheading} /> : null}
        {renderedActions ? <div className="pt-2">{renderedActions}</div> : null}
      </div>
    )
  ) : null;

  return (
    <TootSectionShell
      actions={!hasHeader ? renderedActions : undefined}
      className="relative"
      containerClassName="relative"
      header={header}
      id={anchorId ?? undefined}
      rootClassName={parentClass}
      spacing={spacing ?? undefined}
      tone="dark"
      width="wide"
    >
      <div className="relative mx-auto grid max-w-6xl items-stretch gap-5 md:grid-cols-2 xl:gap-6">
        {items.map((item, index) => (
          <div className="min-w-0" key={`${item.personName}-${item.quote}-${index}`}>
            <TestimonialCard item={item} />
          </div>
        ))}
      </div>
    </TootSectionShell>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
